import React, { useState, useMemo } from 'react';
import {
  DollarSign, TrendingUp, Receipt, Package, Plus, Search, Filter,
  RefreshCcw, Clock, FileText, CreditCard, Wallet, MoreVertical,
  CheckCircle, XCircle, Edit, Trash2, Download, Send, Eye, ChevronDown,
  AlertCircle, ArrowUpRight, ArrowDownRight, Calendar
} from 'lucide-react';

// Mock Data
const mockInvoices = [
  { id: 'INV-2025-001', customer: 'Marcus Johnson', email: 'marcus@email.com', tier: 'gold', status: 'approved', subtotal: 4500, discount: 90, total: 4410, items: 3, created: '2025-12-10', due: '2025-12-17' },
  { id: 'INV-2025-002', customer: 'Sarah Williams', email: 'sarah@email.com', tier: 'business', status: 'paid', subtotal: 8200, discount: 328, total: 7872, items: 5, created: '2025-12-08', due: '2025-12-15', paidAt: '2025-12-12' },
  { id: 'INV-2025-003', customer: 'James Brown', email: 'james@email.com', tier: 'standard', status: 'draft', subtotal: 2450, discount: 0, total: 2450, items: 2, created: '2025-12-14', due: '2025-12-21' },
  { id: 'INV-2025-004', customer: 'Emily Davis', email: 'emily@email.com', tier: 'diamond', status: 'approved', subtotal: 12500, discount: 375, total: 12125, items: 8, created: '2025-12-09', due: '2025-12-16' },
  { id: 'INV-2025-005', customer: 'Michael Chen', email: 'michael@email.com', tier: 'business_premium', status: 'paid', subtotal: 6800, discount: 340, total: 6460, items: 4, created: '2025-12-05', due: '2025-12-12', paidAt: '2025-12-10' },
  { id: 'INV-2025-006', customer: 'Lisa Anderson', email: 'lisa@email.com', tier: 'gold', status: 'overdue', subtotal: 3200, discount: 64, total: 3136, items: 2, created: '2025-11-28', due: '2025-12-05' },
];

const mockPayments = [
  { id: 'PAY-001', invoice: 'INV-2025-002', customer: 'Sarah Williams', amount: 7872, method: 'card', date: '2025-12-12', reference: 'TXN-4829' },
  { id: 'PAY-002', invoice: 'INV-2025-005', customer: 'Michael Chen', amount: 6460, method: 'bank_transfer', date: '2025-12-10', reference: 'BNK-1923' },
  { id: 'PAY-003', invoice: 'INV-2025-007', customer: 'Robert Taylor', amount: 4200, method: 'cash', date: '2025-12-09', reference: 'CSH-0821' },
  { id: 'PAY-004', invoice: 'INV-2025-008', customer: 'Amanda White', amount: 9500, method: 'card', date: '2025-12-08', reference: 'TXN-5812' },
];

const mockExpenses = [
  { id: 'EXP-001', description: 'Customs Processing Fees', amount: 45000, category: 'customs', vendor: 'JCA', date: '2025-12-10' },
  { id: 'EXP-002', description: 'Delivery Vehicle Fuel', amount: 12500, category: 'transport', vendor: 'Texaco', date: '2025-12-09' },
  { id: 'EXP-003', description: 'Warehouse Rent (Dec)', amount: 85000, category: 'rent', vendor: 'KP Properties', date: '2025-12-01' },
  { id: 'EXP-004', description: 'Packaging Supplies', amount: 8200, category: 'supplies', vendor: 'PackRight JA', date: '2025-12-08' },
];

const mockUninvoiced = [
  { id: 'PKG-001', tracking: 'FS-2025-8421', customer: 'Marcus Johnson', weight: 4.5, rate: 2450, status: 'ready' },
  { id: 'PKG-002', tracking: 'FS-2025-8422', customer: 'Marcus Johnson', weight: 2.1, rate: 1450, status: 'ready' },
  { id: 'PKG-003', tracking: 'FS-2025-8423', customer: 'Emily Davis', weight: 6.8, rate: 3450, status: 'ready' },
  { id: 'PKG-004', tracking: 'FS-2025-8424', customer: 'James Brown', weight: 1.2, rate: 1200, status: 'ready' },
];

const TIER_CONFIG = {
  free: { label: 'Free', discount: 0, color: 'bg-slate-100 text-slate-600' },
  standard: { label: 'Standard', discount: 0, color: 'bg-slate-100 text-slate-600' },
  gold: { label: 'Gold', discount: 2, color: 'bg-yellow-100 text-yellow-700' },
  diamond: { label: 'Diamond', discount: 3, color: 'bg-blue-100 text-blue-700' },
  business: { label: 'Business', discount: 4, color: 'bg-orange-100 text-orange-700' },
  business_premium: { label: 'Business+', discount: 5, color: 'bg-amber-100 text-amber-700' },
  business_platinum: { label: 'Platinum', discount: 10, color: 'bg-purple-100 text-purple-700' },
  enterprise: { label: 'Enterprise', discount: 15, color: 'bg-indigo-100 text-indigo-700' },
  business_elite: { label: 'Elite', discount: 30, color: 'bg-rose-100 text-rose-700' },
  executive: { label: 'Executive', discount: 40, color: 'bg-emerald-100 text-emerald-700' },
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600', icon: FileText },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const CATEGORY_CONFIG = {
  customs: { label: 'Customs', color: 'bg-blue-100 text-blue-700' },
  transport: { label: 'Transport', color: 'bg-orange-100 text-orange-700' },
  rent: { label: 'Rent', color: 'bg-purple-100 text-purple-700' },
  supplies: { label: 'Supplies', color: 'bg-green-100 text-green-700' },
  utilities: { label: 'Utilities', color: 'bg-yellow-100 text-yellow-700' },
  other: { label: 'Other', color: 'bg-slate-100 text-slate-600' },
};

const formatJMD = (amount) => `J$${(amount || 0).toLocaleString()}`;

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, colorClass, trend }) => (
  <div className="finances-stat-card" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="stat-header">
      <div className="stat-info">
        <span className="stat-title">{title}</span>
        <span className="stat-value">{value}</span>
        {subtitle && <span className="stat-subtitle">{subtitle}</span>}
      </div>
      <div className={`stat-icon ${colorClass || ''}`}>
        <Icon size={20} />
      </div>
    </div>
    {trend && (
      <div className={`stat-trend ${trend === 'up' ? 'trend-up' : 'trend-down'}`}>
        {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        <span>{trend === 'up' ? '+12%' : '-8%'} vs last week</span>
      </div>
    )}
  </div>
);

// Invoice Card Component
const InvoiceCard = ({ invoice, onView, onEdit, onApprove, onRecordPayment, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const tierConfig = TIER_CONFIG[invoice.tier] || TIER_CONFIG.standard;
  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="invoice-card" data-testid={`invoice-${invoice.id}`}>
      <div className="invoice-card-header">
        <div className="invoice-id-section">
          <span className="invoice-number">{invoice.id}</span>
          <span className={`invoice-status ${statusConfig.color}`}>
            <StatusIcon size={12} />
            {statusConfig.label}
          </span>
        </div>
        <div className="invoice-menu">
          <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>
            <MoreVertical size={16} />
          </button>
          {showMenu && (
            <div className="menu-dropdown">
              <button onClick={() => { onView(invoice); setShowMenu(false); }}>
                <Eye size={14} /> View
              </button>
              {invoice.status === 'draft' && (
                <button onClick={() => { onEdit(invoice); setShowMenu(false); }}>
                  <Edit size={14} /> Edit
                </button>
              )}
              {invoice.status === 'draft' && (
                <button onClick={() => { onApprove(invoice); setShowMenu(false); }}>
                  <CheckCircle size={14} /> Approve
                </button>
              )}
              {invoice.status === 'approved' && (
                <button onClick={() => { onRecordPayment(invoice); setShowMenu(false); }}>
                  <DollarSign size={14} /> Record Payment
                </button>
              )}
              <button onClick={() => { setShowMenu(false); }}>
                <Download size={14} /> Download PDF
              </button>
              {invoice.status === 'draft' && (
                <button className="delete-action" onClick={() => { onDelete(invoice); setShowMenu(false); }}>
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="invoice-customer">
        <span className="customer-name">{invoice.customer}</span>
        <span className={`customer-tier ${tierConfig.color}`}>{tierConfig.label}</span>
      </div>

      <div className="invoice-details">
        <div className="detail-row">
          <span className="detail-label">Items</span>
          <span className="detail-value">{invoice.items} packages</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Subtotal</span>
          <span className="detail-value">{formatJMD(invoice.subtotal)}</span>
        </div>
        {invoice.discount > 0 && (
          <div className="detail-row discount">
            <span className="detail-label">Discount ({tierConfig.discount}%)</span>
            <span className="detail-value">-{formatJMD(invoice.discount)}</span>
          </div>
        )}
      </div>

      <div className="invoice-total">
        <span className="total-label">Total</span>
        <span className="total-value">{formatJMD(invoice.total)}</span>
      </div>

      <div className="invoice-footer">
        <span className="invoice-date">
          <Calendar size={12} /> Due: {invoice.due}
        </span>
        {invoice.status === 'approved' && (
          <button className="pay-btn" onClick={() => onRecordPayment(invoice)}>
            <DollarSign size={14} /> Record Payment
          </button>
        )}
      </div>
    </div>
  );
};

// Payment Card Component
const PaymentCard = ({ payment }) => {
  const methodIcons = {
    cash: Wallet,
    card: CreditCard,
    bank_transfer: Receipt,
  };
  const MethodIcon = methodIcons[payment.method] || Wallet;

  return (
    <div className="payment-card" data-testid={`payment-${payment.id}`}>
      <div className="payment-header">
        <div className="payment-icon">
          <MethodIcon size={20} />
        </div>
        <div className="payment-info">
          <span className="payment-customer">{payment.customer}</span>
          <span className="payment-invoice">{payment.invoice}</span>
        </div>
      </div>
      <div className="payment-amount">{formatJMD(payment.amount)}</div>
      <div className="payment-meta">
        <span className="payment-method">{payment.method.replace('_', ' ')}</span>
        <span className="payment-date">{payment.date}</span>
      </div>
      {payment.reference && (
        <div className="payment-reference">Ref: {payment.reference}</div>
      )}
    </div>
  );
};

// Expense Card Component
const ExpenseCard = ({ expense, onEdit }) => {
  const categoryConfig = CATEGORY_CONFIG[expense.category] || CATEGORY_CONFIG.other;

  return (
    <div className="expense-card" data-testid={`expense-${expense.id}`}>
      <div className="expense-header">
        <span className={`expense-category ${categoryConfig.color}`}>{categoryConfig.label}</span>
        <button className="expense-edit" onClick={() => onEdit(expense)}>
          <Edit size={14} />
        </button>
      </div>
      <div className="expense-description">{expense.description}</div>
      <div className="expense-amount">{formatJMD(expense.amount)}</div>
      <div className="expense-meta">
        <span className="expense-vendor">{expense.vendor}</span>
        <span className="expense-date">{expense.date}</span>
      </div>
    </div>
  );
};

// Uninvoiced Package Row
const UninvoicedRow = ({ pkg, selected, onSelect }) => (
  <div className={`uninvoiced-row ${selected ? 'selected' : ''}`} onClick={() => onSelect(pkg.id)}>
    <input type="checkbox" checked={selected} onChange={() => onSelect(pkg.id)} />
    <span className="uninvoiced-tracking">{pkg.tracking}</span>
    <span className="uninvoiced-customer">{pkg.customer}</span>
    <span className="uninvoiced-weight">{pkg.weight} lbs</span>
    <span className="uninvoiced-rate">{formatJMD(pkg.rate)}</span>
  </div>
);

export default function TenantFinances() {
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoiceFilter, setInvoiceFilter] = useState('unpaid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);

  // Calculate stats
  const stats = useMemo(() => {
    const totalCollected = mockPayments.reduce((sum, p) => sum + p.amount, 0);
    const pendingAmount = mockInvoices
      .filter(i => i.status === 'approved' || i.status === 'overdue')
      .reduce((sum, i) => sum + i.total, 0);
    const totalExpenses = mockExpenses.reduce((sum, e) => sum + e.amount, 0);
    const profit = totalCollected - totalExpenses;
    const uninvoicedTotal = mockUninvoiced.reduce((sum, p) => sum + p.rate, 0);
    const overdueCount = mockInvoices.filter(i => i.status === 'overdue').length;

    return { totalCollected, pendingAmount, totalExpenses, profit, uninvoicedTotal, overdueCount };
  }, []);

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    let filtered = mockInvoices;
    
    if (invoiceFilter === 'unpaid') {
      filtered = filtered.filter(i => i.status !== 'paid');
    } else if (invoiceFilter === 'paid') {
      filtered = filtered.filter(i => i.status === 'paid');
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(i =>
        i.id.toLowerCase().includes(query) ||
        i.customer.toLowerCase().includes(query) ||
        i.email.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [invoiceFilter, searchQuery]);

  const handleSelectPackage = (id) => {
    setSelectedPackages(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedPackages.length === mockUninvoiced.length) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages(mockUninvoiced.map(p => p.id));
    }
  };

  const selectedTotal = mockUninvoiced
    .filter(p => selectedPackages.includes(p.id))
    .reduce((sum, p) => sum + p.rate, 0);

  const tabs = [
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'uninvoiced', label: 'Uninvoiced', icon: Package, badge: mockUninvoiced.length },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'expenses', label: 'Expenses', icon: Receipt },
  ];

  return (
    <div className="finances-page" data-testid="finances-dashboard">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="refresh-btn">
            <RefreshCcw size={16} />
          </button>
          <div className="header-text">
            <h1>Finances</h1>
            <p>Manage invoices, payments, and expenses</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowExpenseDialog(true)}>
            <Plus size={16} />
            <span>Expense</span>
          </button>
          <button className="btn-primary" onClick={() => setShowCreateInvoice(true)}>
            <Plus size={16} />
            <span>New Invoice</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="finances-stats-grid">
        <StatCard
          title="Income"
          value={formatJMD(stats.totalCollected)}
          subtitle="Total collected"
          icon={DollarSign}
          colorClass="icon-yellow"
          trend="up"
        />
        <StatCard
          title="Pending"
          value={formatJMD(stats.pendingAmount)}
          subtitle={`${stats.overdueCount} overdue`}
          icon={Clock}
          colorClass="icon-orange"
        />
        <StatCard
          title="Expenses"
          value={formatJMD(stats.totalExpenses)}
          subtitle="This month"
          icon={Receipt}
          colorClass="icon-red"
        />
        <StatCard
          title="Profit"
          value={formatJMD(stats.profit)}
          subtitle="Income - Expenses"
          icon={TrendingUp}
          colorClass={stats.profit >= 0 ? 'icon-green' : 'icon-red'}
          trend={stats.profit >= 0 ? 'up' : 'down'}
        />
      </div>

      {/* Main Content */}
      <div className="finances-content">
        {/* Tabs */}
        <div className="finances-tabs">
          <div className="tabs-list">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
                {tab.badge && <span className="tab-badge">{tab.badge}</span>}
              </button>
            ))}
          </div>

          {/* Search & Filters */}
          <div className="tabs-actions">
            {activeTab === 'invoices' && (
              <div className="filter-toggle">
                <button
                  className={invoiceFilter === 'unpaid' ? 'active' : ''}
                  onClick={() => setInvoiceFilter('unpaid')}
                >
                  Unpaid
                </button>
                <button
                  className={invoiceFilter === 'paid' ? 'active' : ''}
                  onClick={() => setInvoiceFilter('paid')}
                >
                  Paid
                </button>
              </div>
            )}
            <div className="search-input">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="invoices-grid">
              {filteredInvoices.length === 0 ? (
                <div className="empty-state">
                  <FileText size={48} />
                  <p>No invoices found</p>
                </div>
              ) : (
                filteredInvoices.map(invoice => (
                  <InvoiceCard
                    key={invoice.id}
                    invoice={invoice}
                    onView={() => {}}
                    onEdit={() => {}}
                    onApprove={() => {}}
                    onRecordPayment={() => {}}
                    onDelete={() => {}}
                  />
                ))
              )}
            </div>
          )}

          {/* Uninvoiced Tab */}
          {activeTab === 'uninvoiced' && (
            <div className="uninvoiced-section">
              <div className="uninvoiced-header">
                <div className="select-all">
                  <input
                    type="checkbox"
                    checked={selectedPackages.length === mockUninvoiced.length}
                    onChange={handleSelectAll}
                  />
                  <span>Select All ({mockUninvoiced.length} packages)</span>
                </div>
                {selectedPackages.length > 0 && (
                  <div className="bulk-actions">
                    <span className="selected-total">
                      Selected: {selectedPackages.length} packages ({formatJMD(selectedTotal)})
                    </span>
                    <button className="btn-primary">
                      <FileText size={14} />
                      Create Invoice
                    </button>
                  </div>
                )}
              </div>

              <div className="uninvoiced-table">
                <div className="table-header">
                  <span></span>
                  <span>Tracking #</span>
                  <span>Customer</span>
                  <span>Weight</span>
                  <span>Rate</span>
                </div>
                {mockUninvoiced.map(pkg => (
                  <UninvoicedRow
                    key={pkg.id}
                    pkg={pkg}
                    selected={selectedPackages.includes(pkg.id)}
                    onSelect={handleSelectPackage}
                  />
                ))}
              </div>

              <div className="uninvoiced-summary">
                <span>Total Uninvoiced:</span>
                <span className="total-amount">{formatJMD(stats.uninvoicedTotal)}</span>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="payments-grid">
              {mockPayments.length === 0 ? (
                <div className="empty-state">
                  <CreditCard size={48} />
                  <p>No payments recorded</p>
                </div>
              ) : (
                mockPayments.map(payment => (
                  <PaymentCard key={payment.id} payment={payment} />
                ))
              )}
            </div>
          )}

          {/* Expenses Tab */}
          {activeTab === 'expenses' && (
            <div className="expenses-grid">
              {mockExpenses.length === 0 ? (
                <div className="empty-state">
                  <Receipt size={48} />
                  <p>No expenses recorded</p>
                  <button className="btn-primary" onClick={() => setShowExpenseDialog(true)}>
                    <Plus size={16} />
                    Record Expense
                  </button>
                </div>
              ) : (
                mockExpenses.map(expense => (
                  <ExpenseCard key={expense.id} expense={expense} onEdit={() => {}} />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
